import { Icon } from "../components/common/Icon";
import { PageHero } from "../components/common/PageHero";
import { SmartLink } from "../components/common/SmartLink";
import { useDocumentMeta } from "../lib/useDocumentMeta";

const contactHref = "/contact";

const capabilities = [
  "Deploy repeatable AWS-first baselines for regulated workloads",
  "Keep infrastructure changes tied to approved patterns and guardrails",
  "Detect drift before it becomes audit or operational debt",
  "Drive remediation back toward the approved environment state"
];

const workflow = [
  {
    icon: "document" as const,
    title: "Baseline",
    copy: "Start from a known environment pattern instead of rebuilding compliance architecture from scratch."
  },
  {
    icon: "shield" as const,
    title: "Guardrail",
    copy: "Keep networking, identity, logging, and workload changes inside approved operating boundaries."
  },
  {
    icon: "check-circle" as const,
    title: "Operate",
    copy: "Surface drift and remediation work so teams can keep shipping without losing audit readiness."
  }
];

export function RapidFactoryPage() {
  useDocumentMeta({
    canonicalPath: "/rapid-factory",
    description:
      "RapidFactory is Holocron's compliant environment factory for AWS-first regulated workloads and CMMC Level 2 operations.",
    title: "RapidFactory | Holocron Security"
  });

  return (
    <>
      <PageHero
        actions={[
          { href: contactHref, label: "Build a CMMC Environment", variant: "primary" },
          { href: contactHref, label: "Request a Platform Demo", variant: "secondary" }
        ]}
        copy="RapidFactory gives regulated teams repeatable AWS-first baselines, guardrails, drift awareness, and remediation workflows."
        eyebrow="RapidFactory"
        title="Build compliant environments."
      >
        <div className="device-panel-screen">
          <img alt="RapidFactory regulated environment interface" src="/assets/img/home-1.png" />
        </div>
      </PageHero>

      <section className="surface-dark">
        <div className="section-shell detail-split">
          <div className="split-copy">
            <p className="section-kicker">Why it matters</p>
            <h2 className="section-title">Stop rebuilding compliance from scratch.</h2>
            <div className="stack-copy">
              <p>
                RapidFactory turns regulated environment delivery into a repeatable operating pattern for teams that
                need secure cloud capacity without months of bespoke architecture work.
              </p>
              <p>
                It is designed for organizations under defense-sector requirements that need a baseline they can
                operate, inspect, and maintain over time.
              </p>
            </div>
          </div>
          <div className="fit-panel fit-panel--dark">
            <ul className="bullet-list">
              {capabilities.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="surface-gradient surface-gradient--control">
        <div className="section-shell">
          <div className="use-case-grid">
            {workflow.map((card) => (
              <article key={card.title} className="use-case-card">
                <div className="icon-badge">
                  <Icon name={card.icon} />
                </div>
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-dark">
        <div className="section-shell split-layout">
          <div className="device-panel">
            <div className="device-panel-screen">
              <img alt="RapidFactory status and environment operations" src="/assets/img/status-1.png" />
            </div>
          </div>
          <div className="split-copy">
            <p className="section-kicker">Paired with KyberDocs</p>
            <h2 className="section-title">Build the environment, then keep the proof current.</h2>
            <div className="stack-copy">
              <p>
                RapidFactory handles the environment delivery and operating workflow. KyberDocs supports the evidence,
                endpoint remediation, and audit-readiness layer around it.
              </p>
              <p>
                Holocron keeps the authority boundary clear: infrastructure changes should be controlled, observable,
                and tied to evidence that can be reviewed later.
              </p>
            </div>
            <div className="hero-actions">
              <SmartLink className="button primary" href={contactHref}>
                See RapidFactory + KyberDocs
              </SmartLink>
              <SmartLink className="button secondary" href="/kyber-docs">
                Explore KyberDocs
              </SmartLink>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
